import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePdf } from "../../contexts/pdfcontext";
import Loader from "../loader/loader";

const ERROR_AUTO_CLOSE_MS = 1 * 60 * 1000; // auto-close error notifications after 1 minute

const GlobalPdfNotification = () => {
  const navigate = useNavigate();
  const { getAllPdfNotifications, clearPdfNotification } = usePdf();
  const [navigatingToPdf, setNavigatingToPdf] = useState(false);
  const errorTimersRef = useRef({});

  const allNotifications = getAllPdfNotifications();
  const notificationEntries = Object.entries(allNotifications);

  // Cleanup loader state on component unmount
  useEffect(() => {
    return () => {
      setNavigatingToPdf(false);
    };
  }, []);

  // Auto-close error notifications after ERROR_AUTO_CLOSE_MS
  useEffect(() => {
    const activeBookIds = new Set(Object.keys(allNotifications));

    Object.entries(allNotifications).forEach(([bookId, notification]) => {
      if (notification.type === 'error' && !errorTimersRef.current[bookId]) {
        errorTimersRef.current[bookId] = setTimeout(() => {
          clearPdfNotification(bookId);
          delete errorTimersRef.current[bookId];
        }, ERROR_AUTO_CLOSE_MS);
      }
    });

    // Clear timers for notifications that were already closed/removed
    Object.keys(errorTimersRef.current).forEach((bookId) => {
      if (!activeBookIds.has(bookId)) {
        clearTimeout(errorTimersRef.current[bookId]);
        delete errorTimersRef.current[bookId];
      }
    });
  }, [allNotifications, clearPdfNotification]);

  // Clear all pending timers on unmount
  useEffect(() => {
    const timers = errorTimersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  if (notificationEntries.length === 0) return null;

  const closePdfNotification = (bookId) => {
    clearPdfNotification(bookId);
  };

  const handleViewPdf = (bookId) => {
    setNavigatingToPdf(true);
    
    try {
      setTimeout(() => {   
        // Navigate first, don't clear notification yet
        navigate(`/pdfviewer/${bookId}`);
        
        // Clear notification after navigation
        setTimeout(() => {
          clearPdfNotification(bookId);
          setNavigatingToPdf(false);
        }, 1000); // Give time for ViewPdf to read the pdfUrl from context
      }, 200); // Increased timeout to ensure loader shows
    } catch (error) {
      console.error('Navigation error:', error);
      setNavigatingToPdf(false);
    }
  };

  return (
    <>
      {navigatingToPdf && <Loader showMessage={true} text="Loading your PDF..." />}
      
      <div className="pdf-notifications-container">
        {notificationEntries.map(([bookId, notification]) => (
          <div 
            key={bookId} 
            className={`pdf-notification-simple ${notification.type === 'success' ? 'bg-color' : ''}`}
            onClick={() => notification.type === 'success' ? handleViewPdf(bookId) : null}
          >
            <span className={`pdf-notification-text ${notification.type === 'success' ? 'clickable' : ''}`}>
              <strong>
                {notification.type === 'success' 
                  ? `${notification.bookName} PDF is ready!\nPlease click here to view.`
                  : notification.type === 'error' 
                    ? (notification.errorMessage || `${notification.bookName} PDF generation failed! Please try again later.`)
                    : `${notification.bookName} PDF is being generated.\nWe'll email you when it's ready – usually within 5–20 minutes.\nYou can safely log out or return later to view your PDF once it’s ready.`
                }
              </strong>
            </span>
            <button 
              className="pdf-notification-close-simple" 
              onClick={(e) => {
                e.stopPropagation();
                closePdfNotification(bookId);
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

export default GlobalPdfNotification;